import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    CardBody,
    Row,
    Col,
    Button,
    Badge,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
    Spinner,
} from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import Loader from '../../../Layout/Loader';

interface PendingUnlockRequestsProps {
    employeeIdProp?: number;
}

export default function PendingUnlockRequests({ employeeIdProp }: PendingUnlockRequestsProps) {
    const serverUrl = localStorage.getItem('serverUrl');
    const [loading, setLoading] = useState<boolean>(true);
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [requests, setRequests] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'Requested' | 'Approved' | 'Rejected'>('Requested');

    // Modals
    const [selectedRequest, setSelectedRequest] = useState<any>(null);
    const [approveModalOpen, setApproveModalOpen] = useState<boolean>(false);
    const [rejectModalOpen, setRejectModalOpen] = useState<boolean>(false);
    const [rejectionRemark, setRejectionRemark] = useState<string>('');

    const fetchRequests = async () => {
        if (!serverUrl) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.getTeamLockRequest}`;
            const params: any = {};
            if (employeeIdProp) params.employee_id = employeeIdProp;

            const res = await getData(url, true, params);
            console.log("Team Lock Requests response:", res);

            const data = res?.data;
            let list: any[] = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (Array.isArray(data?.data)) {
                list = data.data;
            } else if (res?.status === 200 && Array.isArray(data)) {
                list = data;
            }

            setRequests(list);
        } catch (error) {
            console.error("Error fetching lock requests:", error);
            toast.error("Failed to fetch team lock requests");
            setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [serverUrl, employeeIdProp]);

    // Handle Approve
    const handleOpenApprove = (req: any) => {
        setSelectedRequest(req);
        setApproveModalOpen(true);
    };

    const handleConfirmApprove = async () => {
        if (!selectedRequest || !serverUrl) return;
        setActionLoading(true);
        try {
            const url = `${serverUrl}${API.approveRejectAttendanceLockReq}`;
            const payload = {
                request_employee_id: selectedRequest.EmployeeId,
                request_date: selectedRequest.AttendanceDate,
                request_status: 'Approved',
            };

            const response = await postData(url, true, payload);
            const ok = response.status === 200 || response.data?.statusCode === 200;

            if (ok) {
                toast.success(response.data?.message || "Attendance lock request approved & unlocked successfully!");
                setApproveModalOpen(false);
                setSelectedRequest(null);
                fetchRequests();
            } else {
                toast.error(response.data?.message || "Failed to approve lock request");
            }
        } catch (error: any) {
            console.error("Error approving lock request:", error);
            toast.error(error?.message || "An error occurred while approving request");
        } finally {
            setActionLoading(false);
        }
    };

    // Handle Reject
    const handleOpenReject = (req: any) => {
        setSelectedRequest(req);
        setRejectionRemark('');
        setRejectModalOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!selectedRequest || !serverUrl) return;
        if (!rejectionRemark.trim()) {
            toast.error("Please enter a rejection remark");
            return;
        }

        setActionLoading(true);
        try {
            const url = `${serverUrl}${API.approveRejectAttendanceLockReq}`;
            const payload = {
                request_employee_id: selectedRequest.EmployeeId,
                request_date: selectedRequest.AttendanceDate,
                request_status: 'Rejected',
                request_remark: rejectionRemark.trim(),
            };

            const response = await postData(url, true, payload);
            const ok = response.status === 200 || response.data?.statusCode === 200;

            if (ok) {
                toast.success(response.data?.message || "Attendance lock request rejected successfully.");
                setRejectModalOpen(false);
                setRejectionRemark('');
                setSelectedRequest(null);
                fetchRequests();
            } else {
                toast.error(response.data?.message || "Failed to reject lock request");
            }
        } catch (error: any) {
            console.error("Error rejecting lock request:", error);
            toast.error(error?.message || "An error occurred while rejecting request");
        } finally {
            setActionLoading(false);
        }
    };

    // Filtered data
    const filteredRequests = useMemo(() => {
        return requests.filter((req) => {
            const empName = (
                req?.EmployeeRelatedByEmployeeId
                    ? `${req.EmployeeRelatedByEmployeeId.FirstName || ''} ${req.EmployeeRelatedByEmployeeId.LastName || ''}`
                    : req?.EmployeeName || ''
            ).toLowerCase();
            const dateStr = String(req?.AttendanceDate || '').toLowerCase();
            const reason = String(req?.EmployeeRelatedByEmployeeId?.Lockedreason || req?.Lockedreason || '').toLowerCase();
            const remark = String(req?.RequestRemark || '').toLowerCase();
            const status = String(req?.RequestStatus || '').toLowerCase();

            const matchesSearch =
                !searchTerm.trim() ||
                empName.includes(searchTerm.toLowerCase()) ||
                dateStr.includes(searchTerm.toLowerCase()) ||
                reason.includes(searchTerm.toLowerCase()) ||
                remark.includes(searchTerm.toLowerCase());

            const matchesStatus =
                statusFilter === 'all' ||
                status === statusFilter.toLowerCase();

            return matchesSearch && matchesStatus;
        });
    }, [requests, searchTerm, statusFilter]);

    const countRequested = useMemo(() => requests.filter(r => (r.RequestStatus || '').toLowerCase() === 'requested').length, [requests]);
    const countApproved = useMemo(() => requests.filter(r => (r.RequestStatus || '').toLowerCase() === 'approved').length, [requests]);
    const countRejected = useMemo(() => requests.filter(r => (r.RequestStatus || '').toLowerCase() === 'rejected').length, [requests]);

    return (
        <Card className="border-0 shadow-sm">
            <CardBody className="p-3">
                {/* Header & Controls */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2 border-bottom">
                    <div>
                        <h5 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                            <FeatherIconCom iconName="Unlock" size={20} className="text-primary" />
                            <span>Attendance Unlock Requests</span>
                        </h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                            Review, approve, or reject locked date attendance requests from team members
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <Button
                            color="outline-secondary"
                            size="sm"
                            className="d-flex align-items-center gap-1"
                            onClick={fetchRequests}
                            disabled={loading}
                        >
                            <FeatherIconCom iconName="RefreshCw" size={14} className={loading ? "spin" : ""} />
                            <span>Refresh</span>
                        </Button>
                    </div>
                </div>

                {/* Filters Row */}
                <Row className="g-2 mb-3 align-items-center">
                    <Col xs={12} md={5}>
                        <div className="position-relative">
                            <Input
                                type="text"
                                placeholder="Search by name, date, reason or remark..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ paddingLeft: '34px', fontSize: '0.9rem' }}
                            />
                            <div
                                className="position-absolute text-muted"
                                style={{ left: '10px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                <FeatherIconCom iconName="Search" size={15} />
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} md={7}>
                        <div className="d-flex gap-1 flex-wrap justify-content-md-end">
                            <Button
                                size="sm"
                                color={statusFilter === 'Requested' ? 'primary' : 'outline-primary'}
                                onClick={() => setStatusFilter('Requested')}
                                className="d-flex align-items-center gap-1"
                            >
                                <span>Pending</span>
                                <Badge color="light" className="text-dark rounded-pill px-2">
                                    {countRequested}
                                </Badge>
                            </Button>
                            <Button
                                size="sm"
                                color={statusFilter === 'Approved' ? 'success' : 'outline-success'}
                                onClick={() => setStatusFilter('Approved')}
                                className="d-flex align-items-center gap-1"
                            >
                                <span>Approved</span>
                                <Badge color="light" className="text-dark rounded-pill px-2">
                                    {countApproved}
                                </Badge>
                            </Button>
                            <Button
                                size="sm"
                                color={statusFilter === 'Rejected' ? 'danger' : 'outline-danger'}
                                onClick={() => setStatusFilter('Rejected')}
                                className="d-flex align-items-center gap-1"
                            >
                                <span>Rejected</span>
                                <Badge color="light" className="text-dark rounded-pill px-2">
                                    {countRejected}
                                </Badge>
                            </Button>
                            <Button
                                size="sm"
                                color={statusFilter === 'all' ? 'secondary' : 'outline-secondary'}
                                onClick={() => setStatusFilter('all')}
                                className="d-flex align-items-center gap-1"
                            >
                                <span>All</span>
                                <Badge color="light" className="text-dark rounded-pill px-2">
                                    {requests.length}
                                </Badge>
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Content */}
                {loading ? (
                    <div className="py-5 text-center">
                        <Loader height="200px" />
                    </div>
                ) : filteredRequests.length === 0 ? (
                    <div className="py-5 text-center text-muted">
                        <div className="mb-2">
                            <FeatherIconCom iconName="Inbox" size={40} className="text-muted opacity-50" />
                        </div>
                        <h6 className="fw-semibold">No unlock requests found</h6>
                        <p style={{ fontSize: '0.85rem' }}>
                            {searchTerm || statusFilter !== 'all'
                                ? 'No requests match the selected search or filter.'
                                : 'There are currently no attendance unlock requests from team members.'}
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th style={{ minWidth: '160px' }}>Employee</th>
                                    <th style={{ minWidth: '120px' }}>Locked Date</th>
                                    <th style={{ minWidth: '160px' }}>Lock Reason</th>
                                    <th style={{ minWidth: '180px' }}>Employee Remark</th>
                                    <th style={{ minWidth: '140px' }}>Submitted On</th>
                                    <th style={{ minWidth: '120px' }}>Start Info</th>
                                    <th style={{ minWidth: '110px' }}>Status</th>
                                    <th style={{ minWidth: '180px', textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRequests.map((req, index) => {
                                    const employeeName = req?.EmployeeRelatedByEmployeeId
                                        ? `${(req.EmployeeRelatedByEmployeeId.FirstName || '').trim()} ${(req.EmployeeRelatedByEmployeeId.LastName || '').trim()}`.trim()
                                        : req?.EmployeeName || `Employee #${req.EmployeeId}`;

                                    const lockReason = req?.EmployeeRelatedByEmployeeId?.Lockedreason || req?.Lockedreason || '-';
                                    const formattedDate = moment(req?.AttendanceDate).isValid()
                                        ? moment(req?.AttendanceDate).format('DD MMM YYYY')
                                        : req?.AttendanceDate || '-';

                                    const submittedOn = moment(req?.UpdatedAt || req?.CreatedAt).isValid()
                                        ? moment(req?.UpdatedAt || req?.CreatedAt).format('DD MMM, hh:mm A')
                                        : '-';

                                    const status = req?.RequestStatus || 'Pending';
                                    const isRequested = status === 'Requested';
                                    const isApproved = status === 'Approved';
                                    const isRejected = status === 'Rejected';

                                    return (
                                        <tr key={req.AttendanceId ? `${req.AttendanceId}-${index}` : index}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                                        style={{
                                                            width: '34px',
                                                            height: '34px',
                                                            backgroundColor: '#0d6efd',
                                                            fontSize: '0.8rem',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        {employeeName.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                                                            {employeeName}
                                                        </div>
                                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                            ID: {req.EmployeeId}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
                                                    {formattedDate}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-danger fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                    {lockReason}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                                                    {req.RequestRemark || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                    {submittedOn}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ fontSize: '0.8rem' }}>
                                                    {req.StartTime && (
                                                        <div>
                                                            <span className="text-muted">Time: </span>
                                                            <span className="fw-semibold">
                                                                {moment(req.StartTime, 'HH:mm:ss').isValid()
                                                                    ? moment(req.StartTime, 'HH:mm:ss').format('hh:mm A')
                                                                    : req.StartTime}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {req.Stownname && (
                                                        <div>
                                                            <span className="text-muted">Town: </span>
                                                            <span className="fw-semibold">{req.Stownname}</span>
                                                        </div>
                                                    )}
                                                    {!req.StartTime && !req.Stownname && '-'}
                                                </div>
                                            </td>
                                            <td>
                                                {isRequested && (
                                                    <Badge color="warning" className="text-dark px-2 py-1">
                                                        Requested
                                                    </Badge>
                                                )}
                                                {isApproved && (
                                                    <Badge color="success" className="px-2 py-1">
                                                        Approved
                                                    </Badge>
                                                )}
                                                {isRejected && (
                                                    <Badge color="danger" className="px-2 py-1">
                                                        Rejected
                                                    </Badge>
                                                )}
                                                {!isRequested && !isApproved && !isRejected && (
                                                    <Badge color="secondary" className="px-2 py-1">
                                                        {status}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {isRequested ? (
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Button
                                                            color="success"
                                                            size="sm"
                                                            className="px-2 py-1 d-flex align-items-center gap-1"
                                                            onClick={() => handleOpenApprove(req)}
                                                            disabled={actionLoading}
                                                            style={{ fontSize: '0.8rem' }}
                                                        >
                                                            <FeatherIconCom iconName="Check" size={13} />
                                                            <span>Approve</span>
                                                        </Button>
                                                        <Button
                                                            color="danger"
                                                            outline
                                                            size="sm"
                                                            className="px-2 py-1 d-flex align-items-center gap-1"
                                                            onClick={() => handleOpenReject(req)}
                                                            disabled={actionLoading}
                                                            style={{ fontSize: '0.8rem' }}
                                                        >
                                                            <FeatherIconCom iconName="X" size={13} />
                                                            <span>Reject</span>
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>
                                                        {isApproved ? 'Unlocked' : isRejected ? 'Rejected' : 'Completed'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardBody>

            {/* Approve Confirmation Modal */}
            <Modal isOpen={approveModalOpen} toggle={() => !actionLoading && setApproveModalOpen(false)} centered>
                <ModalHeader toggle={() => !actionLoading && setApproveModalOpen(false)}>
                    Review Unlock Request
                </ModalHeader>
                <ModalBody>
                    {selectedRequest && (
                        <div>
                            <div className="alert alert-info d-flex align-items-center gap-2 mb-3">
                                <FeatherIconCom iconName="AlertCircle" size={20} className="text-info flex-shrink-0" />
                                <div>
                                    You are about to unlock attendance for{' '}
                                    <strong>
                                        {selectedRequest.EmployeeRelatedByEmployeeId
                                            ? `${selectedRequest.EmployeeRelatedByEmployeeId.FirstName || ''} ${selectedRequest.EmployeeRelatedByEmployeeId.LastName || ''}`
                                            : selectedRequest.EmployeeName || `Employee #${selectedRequest.EmployeeId}`}
                                    </strong>{' '}
                                    on{' '}
                                    <strong>
                                        {moment(selectedRequest.AttendanceDate).isValid()
                                            ? moment(selectedRequest.AttendanceDate).format('DD MMM YYYY')
                                            : selectedRequest.AttendanceDate}
                                    </strong>.
                                </div>
                            </div>
                            <div className="bg-light p-3 rounded mb-2" style={{ fontSize: '0.875rem' }}>
                                <div className="mb-1">
                                    <span className="text-muted">Lock Reason: </span>
                                    <span className="fw-semibold text-danger">
                                        {selectedRequest.EmployeeRelatedByEmployeeId?.Lockedreason || selectedRequest.Lockedreason || '-'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-muted">Employee Remark: </span>
                                    <span className="fw-semibold">
                                        {selectedRequest.RequestRemark || '-'}
                                    </span>
                                </div>
                            </div>
                            <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                                Are you sure you want to approve this request and unlock the date for this user?
                            </p>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        outline
                        onClick={() => setApproveModalOpen(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="success"
                        onClick={handleConfirmApprove}
                        disabled={actionLoading}
                        className="d-flex align-items-center gap-1"
                    >
                        {actionLoading ? <Spinner size="sm" /> : <FeatherIconCom iconName="Check" size={14} />}
                        <span>Approve & Unlock</span>
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Reject Modal */}
            <Modal isOpen={rejectModalOpen} toggle={() => !actionLoading && setRejectModalOpen(false)} centered>
                <ModalHeader toggle={() => !actionLoading && setRejectModalOpen(false)}>
                    Reject Unlock Request
                </ModalHeader>
                <ModalBody>
                    {selectedRequest && (
                        <div>
                            <div className="mb-3" style={{ fontSize: '0.875rem' }}>
                                <div>
                                    Rejecting request for{' '}
                                    <strong>
                                        {selectedRequest.EmployeeRelatedByEmployeeId
                                            ? `${selectedRequest.EmployeeRelatedByEmployeeId.FirstName || ''} ${selectedRequest.EmployeeRelatedByEmployeeId.LastName || ''}`
                                            : selectedRequest.EmployeeName || `Employee #${selectedRequest.EmployeeId}`}
                                    </strong>{' '}
                                    ({moment(selectedRequest.AttendanceDate).isValid()
                                        ? moment(selectedRequest.AttendanceDate).format('DD MMM YYYY')
                                        : selectedRequest.AttendanceDate})
                                </div>
                            </div>

                            <FormGroup>
                                <Label for="rejectionRemark" className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                                    Rejection Remark <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    id="rejectionRemark"
                                    type="textarea"
                                    rows={3}
                                    placeholder="Enter reason for rejecting this unlock request..."
                                    value={rejectionRemark}
                                    onChange={(e) => setRejectionRemark(e.target.value)}
                                    disabled={actionLoading}
                                />
                            </FormGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        outline
                        onClick={() => setRejectModalOpen(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={handleConfirmReject}
                        disabled={actionLoading}
                        className="d-flex align-items-center gap-1"
                    >
                        {actionLoading ? <Spinner size="sm" /> : <FeatherIconCom iconName="X" size={14} />}
                        <span>Reject Request</span>
                    </Button>
                </ModalFooter>
            </Modal>
        </Card>
    );
}
